import React from 'react'
import { FileRejection, useDropzone } from 'react-dropzone'

import {
  Box,
  Chevron,
  Icon,
  PopoverMenu,
  useUnsafeCallback,
  useUnsafeEffect,
} from '@factorialco/deprecated-design-system'
import { $isLinkNode, TOGGLE_LINK_COMMAND } from '@lexical/link'
import {
  $isListNode,
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  ListNode,
  REMOVE_LIST_COMMAND,
} from '@lexical/list'
import type { HeadingTagType } from '@lexical/rich-text'
import {
  $createHeadingNode,
  $createQuoteNode,
  $isHeadingNode,
  $isQuoteNode,
} from '@lexical/rich-text'
import { $wrapNodes } from '@lexical/selection'
import { $getNearestNodeOfType, mergeRegister } from '@lexical/utils'
import {
  $createParagraphNode,
  $getSelection,
  $isElementNode,
  $isRangeSelection,
  $isTextNode,
  COMMAND_PRIORITY_CRITICAL,
  FORMAT_TEXT_COMMAND,
  LexicalEditor,
  SELECTION_CHANGE_COMMAND,
} from 'lexical'

import type { onFiles } from 'modules/core/components/TextEditor/Editor'
import { useEditor } from 'modules/core/components/TextEditor/useEditor'
import { getSelectedNode } from 'modules/core/components/TextEditor/utils/getSelectedNode'
import { sanitizeUrl } from 'modules/core/components/TextEditor/utils/url'
import { AlignmentFormatDropDown, SupportedAlignmentTypes } from './AlignmentFormatDropDown'
import { ATTACH_FILES_COMMAND } from './FileAttachment'
import './ToolbarPlugin.css'

/*
MB-to-byte scale factor.
*/
const MB_IN_BYTES = 1024 * 1024

type SupportedBlockTypes = 'paragraph' | 'h1' | 'h2' | 'h3' | 'bullet' | 'number' | 'quote'

function BlockFormatDropDown({
  editor,
  blockType,
}: {
  blockType: SupportedBlockTypes
  editor: LexicalEditor
}): JSX.Element {
  const blockTypeIcon: Record<
    SupportedBlockTypes,
    React.ComponentProps<typeof Icon.Medium>['icon']
  > = {
    paragraph: 'text-size',
    h1: 'h1',
    h2: 'h2',
    h3: 'h3',
    bullet: 'list',
    number: 'ol-list',
    quote: 'quote',
  }

  const formatParagraph = () => {
    if (blockType !== 'paragraph') {
      editor.update(() => {
        const selection = $getSelection()

        if ($isRangeSelection(selection)) {
          $wrapNodes(selection, () => $createParagraphNode())
        }
      })
    }
  }

  const formatHeading = (headingSize: HeadingTagType) => {
    if (blockType !== headingSize) {
      editor.update(() => {
        const selection = $getSelection()

        if ($isRangeSelection(selection)) {
          $wrapNodes(selection, () => $createHeadingNode(headingSize))
        }
      })
    }
  }

  const formatBulletList = () => {
    if (blockType !== 'bullet') {
      editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined)
    } else {
      editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined)
    }
  }

  const formatNumberedList = () => {
    if (blockType !== 'number') {
      editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined)
    } else {
      editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined)
    }
  }

  const formatQuote = () => {
    if (blockType !== 'quote') {
      editor.update(() => {
        const selection = $getSelection()
        if ($isRangeSelection(selection)) {
          $wrapNodes(selection, () => $createQuoteNode())
        }
      })
    } else {
      formatParagraph()
    }
  }

  return (
    <PopoverMenu type="list">
      <PopoverMenu.Trigger>
        <Box className="toolbar-item" flexDirection="row" alignItems="center" gap="s4">
          <Icon.Medium icon={blockTypeIcon[blockType]} color="grey800" />
          <Chevron type="terciary" direction="down" />
        </Box>
      </PopoverMenu.Trigger>
      <PopoverMenu.Content side="bottom" align="start">
        <PopoverMenu.TouchableOption
          title="Normal"
          icon={blockTypeIcon.paragraph}
          onClick={formatParagraph}
        />
        <PopoverMenu.TouchableOption
          title="Heading 1"
          icon={blockTypeIcon.h1}
          onClick={() => formatHeading('h1')}
        />
        <PopoverMenu.TouchableOption
          title="Heading 2"
          icon={blockTypeIcon.h2}
          onClick={() => formatHeading('h2')}
        />
        <PopoverMenu.TouchableOption
          title="Heading 3"
          icon={blockTypeIcon.h3}
          onClick={() => formatHeading('h3')}
        />
        <PopoverMenu.TouchableOption
          title="Bullet List"
          icon={blockTypeIcon.bullet}
          onClick={formatBulletList}
        />
        <PopoverMenu.TouchableOption
          title="Numbered List"
          icon={blockTypeIcon.number}
          onClick={formatNumberedList}
        />
        <PopoverMenu.TouchableOption
          title="Quote"
          icon={blockTypeIcon.quote}
          onClick={formatQuote}
        />
      </PopoverMenu.Content>
    </PopoverMenu>
  )
}

function Divider(): JSX.Element {
  return <div className="divider" />
}

interface ToolbarPluginProps {
  onFiles?: onFiles
  enableBetaFeatures?: boolean
  /**
   * Maximum file size in bytes.
   */
  maxFileSize?: number
  onDropRejected: (rejections: FileRejection[]) => void
}

export default function ToolbarPlugin({
  onFiles,
  enableBetaFeatures = true,
  maxFileSize,
  onDropRejected,
}: Readonly<ToolbarPluginProps>) {
  const editor = useEditor()
  const [canAddLink, setCanAddLink] = React.useState(false)
  const [blockType, setBlockType] = React.useState<SupportedBlockTypes>('paragraph')
  const [alignmentType, setAlignmentType] = React.useState<SupportedAlignmentTypes>('left')
  const [isBold, setIsBold] = React.useState(false)
  const [isItalic, setIsItalic] = React.useState(false)
  const [isUnderline, setIsUnderline] = React.useState(false)
  const [isStrikethrough, setIsStrikethrough] = React.useState(false)
  const [isCode, setIsCode] = React.useState(false)
  const [isLink, setIsLink] = React.useState(false)

  const scaledFileSize = maxFileSize ? maxFileSize * MB_IN_BYTES : undefined

  const { getRootProps, open, getInputProps } = useDropzone({
    noClick: true,
    noKeyboard: true,
    maxSize: scaledFileSize,
    onDrop: (files) => editor.dispatchCommand(ATTACH_FILES_COMMAND, files),
    onDropRejected,
    accept: {
      'image/*': [
        '.apng',
        '.bmp',
        '.gif',
        '.jpeg',
        '.jpg',
        '.png',
        '.svg',
        '.tif',
        '.tiff',
        '.webp',
        '.pdf',
        '.doc',
        '.docx',
        '.zip',
        '.zipx',
        '.arj',
        '.rar',
        '.tar.gz',
        '.tgz',
        '.xlsx',
        '.xls',
      ],
      'video/*': ['.avi', '.mkv', '.mov', '.mpeg', '.mp4', '.webm', '.wmv'],
    },
  })

  const updateToolbar = useUnsafeCallback(() => {
    const selection = $getSelection()
    if ($isRangeSelection(selection)) {
      const anchorNode = selection.anchor.getNode()

      const element =
        anchorNode.getKey() === 'root' ? anchorNode : anchorNode.getTopLevelElementOrThrow()

      const elementKey = element.getKey()
      const elementDOM = editor.getElementByKey(elementKey)

      setIsBold(selection.hasFormat('bold'))
      setIsItalic(selection.hasFormat('italic'))
      setIsUnderline(selection.hasFormat('underline'))
      setIsStrikethrough(selection.hasFormat('strikethrough'))
      setIsCode(selection.hasFormat('code'))

      const node = getSelectedNode(selection)
      const parent = node.getParent()

      setCanAddLink($isTextNode(node) || ($isElementNode(node) && !node.isEmpty()))

      if ($isLinkNode(parent) || $isLinkNode(node)) {
        setIsLink(true)
      } else {
        setIsLink(false)
      }

      if (elementDOM !== null) {
        if ($isListNode(element)) {
          const parentList = $getNearestNodeOfType(anchorNode, ListNode)
          const type = parentList ? parentList.getListType() : element.getListType()
          setBlockType(type as SupportedBlockTypes)
        } else {
          const type = $isHeadingNode(element)
            ? element.getTag()
            : $isQuoteNode(element)
              ? 'quote'
              : 'paragraph'

          setBlockType(type as SupportedBlockTypes)

          if ($isElementNode(element)) {
            const alignType = element.getFormatType()
            setAlignmentType(alignType as SupportedAlignmentTypes)
          }
        }
      }
    }
  }, [editor])

  const insertLink = useUnsafeCallback(() => {
    const url = isLink ? null : sanitizeUrl('')

    editor.dispatchCommand(TOGGLE_LINK_COMMAND, url)
  }, [editor, isLink])

  useUnsafeEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          updateToolbar()
        })
      }),
      editor.registerCommand(
        TOGGLE_LINK_COMMAND,
        (payload) => {
          setIsLink(payload !== null)

          return false
        },
        COMMAND_PRIORITY_CRITICAL
      ),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        (_payload) => {
          updateToolbar()

          return false
        },
        COMMAND_PRIORITY_CRITICAL
      )
    )
  }, [editor])

  if (!editor.isEditable()) {
    return null
  }

  return (
    <div className="toolbar">
      <BlockFormatDropDown blockType={blockType} editor={editor} />
      {enableBetaFeatures && (
        <AlignmentFormatDropDown editor={editor} alignmentType={alignmentType} />
      )}
      <Divider />
      <button
        onClick={() => {
          editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold')
        }}
        className={'toolbar-item spaced ' + (isBold ? 'active' : '')}
        title="Bold (Ctrl+B)"
        aria-label="Format text as bold. Shortcut: Ctrl+B"
        type="button"
      >
        <Icon.Medium icon="bold" color="grey800" />
      </button>
      <button
        onClick={() => {
          editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic')
        }}
        className={'toolbar-item spaced ' + (isItalic ? 'active' : '')}
        title="Italic (Ctrl+I)"
        aria-label="Format text as italics. Shortcut: Ctrl+I"
        type="button"
      >
        <Icon.Medium icon="italic" color="grey800" />
      </button>
      {enableBetaFeatures && (
        <>
          <button
            onClick={() => {
              editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline')
            }}
            className={'toolbar-item spaced ' + (isUnderline ? 'active' : '')}
            title="Underline (Ctrl+U)"
            aria-label="Format text to underlined. Shortcut: Ctrl+U"
            type="button"
          >
            <Icon.Medium icon="underline" color="grey800" />
          </button>
          <button
            onClick={() => {
              editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'strikethrough')
            }}
            className={'toolbar-item spaced ' + (isStrikethrough ? 'active' : '')}
            title="Strikethrough"
            aria-label="Format text with a strikethrough"
            type="button"
          >
            <Icon.Medium icon="strikethrough" color="grey800" />
          </button>
        </>
      )}
      <button
        onClick={() => {
          editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'code')
        }}
        className={'toolbar-item ' + (isCode ? 'active' : '')}
        title="Insert code block"
        aria-label="Insert code block"
        type="button"
      >
        <Icon.Medium icon="code" color="grey800" />
      </button>
      <button
        type="button"
        onClick={insertLink}
        className={'toolbar-item spaced ' + (isLink ? 'active' : '')}
        disabled={!canAddLink}
        aria-label="Insert link"
        title="Insert link"
      >
        <Icon.Medium
          icon={isLink ? 'link-remove' : 'link'}
          color={canAddLink ? 'grey800' : 'grey600'}
        />
      </button>
      {onFiles && (
        <>
          <Divider />
          <div {...getRootProps()}>
            <input {...getInputProps()} />
            <button
              onClick={open}
              className="toolbar-item"
              title="Attach files"
              aria-label="Attach files"
              type="button"
            >
              <Icon.Medium icon="paperclip" color="grey800" />
            </button>
          </div>
        </>
      )}
    </div>
  )
}
